import React, { useState } from 'react';
import { _T, C, CSS } from '../../shared/theme';

const BIZ_TOUR_KEY = 'sl_biz_tour_done';

const SLIDES = [
  {
    icon: '🏢',
    titleNL: 'Welkom in je Business Dashboard',
    titleEN: 'Welcome to your Business Dashboard',
    bodyNL: 'Je hebt je bedrijf aangemaakt! Dit dashboard is jouw centrale plek voor teamwelzijn, prestaties en HR — alles op één plek.',
    bodyEN: 'You\'ve created your business! This dashboard is your central hub for team wellness, performance and HR — all in one place.',
    color: '#C8A84B',
  },
  {
    icon: '🏠',
    titleNL: 'Home — Dashboard & Feed',
    titleEN: 'Home — Dashboard & Feed',
    bodyNL: 'Het Dashboard toont realtime welzijnsscores van je team. De Team Feed is voor updates en aankondigingen. De Agenda synchroniseert teamafspraken.',
    bodyEN: 'The Dashboard shows real-time wellness scores for your team. The Team Feed is for updates and announcements. The Agenda syncs team appointments.',
    color: '#5B9ED0',
    hint: '📍 Pulsscores worden automatisch bijgewerkt als leden check-ins doen',
    hintEN: '📍 Pulse scores update automatically as members submit check-ins',
  },
  {
    icon: '👥',
    titleNL: 'Team — Leden & Uitnodigen',
    titleEN: 'Team — Members & Invite',
    bodyNL: 'Nodig teamleden uit via een unieke link. Wijs rollen toe: Admin, Manager, HR of Lid. Bekijk focus- en senseScores per teamlid.',
    bodyEN: 'Invite team members via a unique link. Assign roles: Admin, Manager, HR or Member. View focus and sense scores per team member.',
    color: '#2A9E82',
    hint: '📍 Elke rol geeft toegang tot andere secties van het dashboard',
    hintEN: '📍 Each role gives access to different sections of the dashboard',
  },
  {
    icon: '💚',
    titleNL: 'Welzijn — Programma\'s & Training',
    titleEN: 'Wellness — Programs & Training',
    bodyNL: 'Maak groeiprogramma\'s met taken voor je team. Training biedt modules voor Soft Skills, Hard Skills, Sociale Vaardigheden en Focus. Activiteit toont voltooide taken.',
    bodyEN: 'Create growth programs with tasks for your team. Training offers modules for Soft Skills, Hard Skills, Social Skills and Focus. Activity shows completed tasks.',
    color: '#4AC87A',
    hint: '📍 Leden verdienen badges als ze programma\'s voltooien',
    hintEN: '📍 Members earn badges as they complete programs',
  },
  {
    icon: '📊',
    titleNL: 'Inzichten — Pulse & Rapporten',
    titleEN: 'Insights — Pulse & Reports',
    bodyNL: 'Voer pulse-enquêtes uit om teamwelzijn te meten. Bekijk gedetailleerde rapporten, ROI-berekeningen en de RI&E / eNPS analyse.',
    bodyEN: 'Run pulse surveys to measure team wellness. View detailed reports, ROI calculations and the RI&E / eNPS analysis.',
    color: '#9B59B6',
    hint: '📍 Anoniemiteit van pulse data beschermt de privacy van je team',
    hintEN: '📍 Pulse data anonymity protects your team\'s privacy',
  },
  {
    icon: '📋',
    titleNL: 'HR — Contracten & Verlof',
    titleEN: 'HR — Contracts & Leave',
    bodyNL: 'Beheer contracten, verlofaanvragen, ziekmeldingen en verlofsaldo\'s. HR-rollen geven specifieke toegang zonder dat managers alles zien.',
    bodyEN: 'Manage contracts, leave requests, sick reports and leave balances. HR roles grant specific access without managers seeing everything.',
    color: '#D4873A',
  },
  {
    icon: '⚙️',
    titleNL: 'Profiel & Instellingen',
    titleEN: 'Profile & Settings',
    bodyNL: 'Upload je logo, schrijf een bedrijfsbeschrijving, voeg openingstijden toe en maak diensten beschikbaar voor boeking. Je publieke pagina is zichtbaar voor de community.',
    bodyEN: 'Upload your logo, write a business description, add opening hours and make services available for booking. Your public page is visible to the community.',
    color: '#C8A84B',
    hint: '📍 Je publieke pagina is zichtbaar via het community-profiel',
    hintEN: '📍 Your public page is visible via community profiles',
  },
];

export default function BusinessIntroModal({ lang, onClose }) {
  const nl = lang === 'nl';
  const [step, setStep] = useState(0);
  const slide = SLIDES[step];
  const isLast = step === SLIDES.length - 1;

  const finish = () => {
    localStorage.setItem(BIZ_TOUR_KEY, '1');
    onClose();
  };

  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, zIndex: 9990, background: 'rgba(5,10,20,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <style>{CSS}</style>
      <div style={{ background: _T.bg2, borderRadius: 24, padding: '28px 24px 24px', width: '100%', maxWidth: 420, border: `1px solid ${slide.color}33`, boxShadow: `0 0 60px ${slide.color}22, 0 24px 64px rgba(0,0,0,0.6)`, position: 'relative' }}>

        {/* Skip */}
        <button onClick={finish} style={{ position: 'absolute', top: 14, right: 14, background: 'none', border: 'none', color: C.silver, fontSize: 12, cursor: 'pointer', opacity: 0.6 }}>
          {nl ? 'Overslaan' : 'Skip'}
        </button>

        {/* Step dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 5, marginBottom: 20 }}>
          {SLIDES.map((_, i) => (
            <div key={i} onClick={() => setStep(i)} style={{ width: i === step ? 18 : 6, height: 6, borderRadius: 3, background: i === step ? slide.color : `${C.silver}40`, cursor: 'pointer', transition: 'all 0.2s' }} />
          ))}
        </div>

        {/* Icon */}
        <div style={{ textAlign: 'center', fontSize: 52, marginBottom: 16, filter: `drop-shadow(0 0 16px ${slide.color}88)` }}>
          {slide.icon}
        </div>

        {/* Title */}
        <div style={{ textAlign: 'center', color: slide.color, fontSize: 17, fontWeight: 800, fontFamily: 'Georgia,serif', marginBottom: 10 }}>
          {nl ? slide.titleNL : slide.titleEN}
        </div>

        {/* Body */}
        <div style={{ textAlign: 'center', color: C.silver2, fontSize: 13, lineHeight: 1.7, marginBottom: 16 }}>
          {nl ? slide.bodyNL : slide.bodyEN}
        </div>

        {/* Hint */}
        {(nl ? slide.hint : slide.hintEN) && (
          <div style={{ background: `${slide.color}12`, border: `1px solid ${slide.color}33`, borderRadius: 12, padding: '9px 14px', marginBottom: 16, fontSize: 11, color: slide.color, textAlign: 'center' }}>
            {nl ? slide.hint : slide.hintEN}
          </div>
        )}

        {/* Navigation */}
        <div style={{ display: 'flex', gap: 8 }}>
          {step > 0 && (
            <button onClick={() => setStep(s => s - 1)} style={{ flex: 1, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 12, color: C.silver, fontSize: 14, fontWeight: 600, padding: '12px', cursor: 'pointer' }}>
              ‹ {nl ? 'Vorige' : 'Back'}
            </button>
          )}
          {isLast ? (
            <button onClick={finish} style={{ flex: 1, background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontSize: 14, fontWeight: 800, padding: '12px', cursor: 'pointer' }}>
              🚀 {nl ? 'Aan de slag!' : 'Let\'s go!'}
            </button>
          ) : (
            <button onClick={() => setStep(s => s + 1)} style={{ flex: 1, background: `linear-gradient(135deg,${slide.color},${slide.color}cc)`, border: 'none', borderRadius: 12, color: '#0a1220', fontSize: 14, fontWeight: 700, padding: '12px', cursor: 'pointer' }}>
              {nl ? 'Volgende' : 'Next'} ›
            </button>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: 12, fontSize: 10, color: `${C.silver}60` }}>
          {step + 1} / {SLIDES.length}
        </div>
      </div>
    </div>
  );
}

export { BIZ_TOUR_KEY };
